import { DeleteOutlined, EditOutlined, EyeOutlined, SaveOutlined } from "@ant-design/icons";
import { Link, useRouterState } from "@tanstack/react-router";
import { Button } from "antd";
import { useGetBalanceWalletQuery } from "api/TenantSlug/Balance/hooks";
import { diagramSaveAtom } from "components/CC/atoms/diagramSaveAtom.ts";
import { headerNavigationAtom } from "components/UI/DynamicHeaderNavigation/atoms/headerNavigationAtom.ts";
import { TenantLink } from "components/UI/TenantLink/TenantLink.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRoute } from "hooks/useRoute";
import { useUser } from "hooks/useUser.ts";
import { useRecoilValue } from "recoil";
import { BalanceRole, PermissionEnum } from "types/enums";
import { RequiredStaticData } from "utils/createPaldockFileRoute.ts";
import { isRouteAccessible } from "utils/isRouteAccessible.ts";
import { removeTenantSlugParam } from "utils/removeTenantSlugParam.ts";

import { HeaderNavigation } from "../HeaderNavigation/HeaderNavigation";
import { HeaderWallet } from "../HeaderWallet/HeaderWallet";

// Header wallet is only shown for these roles for now — the API already returns
// wallet data for every role the user has (admin/advertiser included), but the
// header UI is scoped down until the other roles are designed.
const HEADER_WALLET_VISIBLE_ROLES: Array<App.Data.BalanceWalletRoleData["role"]> = [BalanceRole.Affiliate];

interface DynamicHeaderNavigationProps {
    onMobileMenuToggle?: () => void;
}

const getActiveTab = (items: Array<string>, currentPath: string) => {
    let activeTab = "";

    for (const item of items) {
        if (currentPath.startsWith(item) && item.length > activeTab.length) {
            activeTab = item;
        }
    }

    return activeTab || currentPath;
};

export const DynamicHeaderNavigation = ({ onMobileMenuToggle }: DynamicHeaderNavigationProps) => {
    const routerState = useRouterState();
    const match = routerState.matches[routerState.matches.length - 1];
    const userUtils = useUser();
    const headerNavigationState = useRecoilValue(headerNavigationAtom);
    const diagramSaveState = useRecoilValue(diagramSaveAtom);
    const { t } = usePaldockTranslation();
    const { t: tC } = usePaldockTranslation("common");
    const permissionUtils = usePermission();
    const routeUtils = useRoute();
    const currentRoute = routeUtils.getCurrentRoute();

    const tenantSlug = userUtils.getActiveTenant()?.slug_name;
    const canViewTenant = permissionUtils.hasPermission(PermissionEnum.TenantView);
    // Wallet balance is a tenant-scoped endpoint — never fetch it for non-tenant pages (e.g. /account).
    const getBalanceWalletQuery = useGetBalanceWalletQuery({ enabled: !!tenantSlug });
    const visibleWalletRoles = (getBalanceWalletQuery.data?.roles ?? []).filter((role) =>
        HEADER_WALLET_VISIBLE_ROLES.includes(role.role)
    );

    if (!headerNavigationState.items?.length) {
        return null;
    }

    const activeKey = tenantSlug ? removeTenantSlugParam(match.fullPath) : routerState.location.pathname;

    const tabItems = headerNavigationState.items
        .filter((item) => {
            if (!item.route?.fullPath) return false;

            if (item.requiredPermissions) {
                return permissionUtils.hasAtLeastOnePermission(item.requiredPermissions);
            }

            const staticData = item.route?.options?.staticData as RequiredStaticData | undefined;
            if (!staticData) return false;
            return isRouteAccessible(staticData, permissionUtils.hasAtLeastOnePermission);
        })
        .map((item) => {
            const routeStaticData = item.route.options.staticData as { label: string };
            const label = t(item.customLabel || routeStaticData.label);

            if (tenantSlug) {
                const key = removeTenantSlugParam(item.route.fullPath);
                return {
                    key,
                    label: (
                        <TenantLink to={key} params={{ tenantSlug, ...currentRoute.params }}>
                            <span className="py-3">{label}</span>
                        </TenantLink>
                    ),
                };
            }

            return {
                key: item.route.fullPath,
                label: (
                    <Link to={item.route.fullPath}>
                        <span className="py-3">{label}</span>
                    </Link>
                ),
            };
        });

    const buttons = (
        <div className="gap-x-5 flex items-center">
            {headerNavigationState.saveFormId && (
                <Button
                    type={"primary"}
                    className={"rounded-md"}
                    htmlType={"submit"}
                    form={headerNavigationState.saveFormId}
                >
                    <SaveOutlined /> {tC("save")}
                </Button>
            )}
            {tenantSlug && diagramSaveState.save && (
                <Button
                    type={"primary"}
                    className={"rounded-md"}
                    onClick={diagramSaveState.save}
                    disabled={!diagramSaveState.hasChanges}
                >
                    <SaveOutlined /> {tC("save")}
                </Button>
            )}
            {headerNavigationState.onDelete && (
                <span
                    role="button"
                    tabIndex={0}
                    className={`cursor-pointer select-none flex items-center gap-1 ${headerNavigationState.deleteLoading ? "opacity-50 pointer-events-none" : ""}`}
                    onClick={headerNavigationState.onDelete}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            headerNavigationState.onDelete?.();
                        }
                    }}
                    aria-disabled={headerNavigationState.deleteLoading}
                >
                    <DeleteOutlined /> {tC("delete")}
                </span>
            )}
            {headerNavigationState.extraButtons}
            {tenantSlug && headerNavigationState.editLink && (
                <TenantLink
                    to={headerNavigationState.editLink.to}
                    params={headerNavigationState.editLink.params}
                    search={headerNavigationState.editLink.search}
                    className={"leading-none"}
                >
                    <EditOutlined /> {tC("edit")}
                </TenantLink>
            )}
            {tenantSlug && headerNavigationState.detailLink && (
                <TenantLink
                    to={headerNavigationState.detailLink.to}
                    params={headerNavigationState.detailLink.params}
                    search={headerNavigationState.detailLink.search}
                    className={"leading-none text-black"}
                >
                    <EyeOutlined /> {headerNavigationState.detailLink.label ?? tC("preview")}
                </TenantLink>
            )}
        </div>
    );

    const bgClass = headerNavigationState.isEdit ? "bg-headerMenuBgHighlighted" : "bg-headerMenuBgDefault";

    return (
        <HeaderNavigation
            buttons={buttons}
            tabItems={tabItems}
            activeTab={getActiveTab(
                tabItems.map(({ key }) => key),
                activeKey
            )}
            tenantSlug={tenantSlug}
            showSettingsLink={canViewTenant}
            className={bgClass}
            onMobileMenuToggle={onMobileMenuToggle}
            walletIndicator={tenantSlug && visibleWalletRoles.length > 0 && <HeaderWallet roles={visibleWalletRoles} />}
        />
    );
};
